import p5 from 'p5'
import { BaseMarker } from './baseMarker'
import { MarkerModes } from '../../types'

class BaseMarkerController<MarkerType extends BaseMarker<any, any, any, any>> {
  marker: MarkerType
  mouseDelta: p5.Vector
  lastMousePosition: p5.Vector
  mouseX: number
  mouseY: number
  mouseCoordinateX: number
  mouseCoordinateY: number

  updateMousePosition() {
    const localCoordinates = this.marker.p.localMousePosition()
    this.mouseX = localCoordinates.x
    this.mouseY = localCoordinates.y
    this.mouseCoordinateX = Math.round(localCoordinates.x)
    this.mouseCoordinateY = Math.round(localCoordinates.y)
  }

  /**
   * Updates the amount the mouse x and mouse y position has moved since last checked.
   */
  updateMouseDelta() {
    const marker = this.marker
    const currentMousePosition = marker.p.createVector(this.mouseCoordinateX, this.mouseCoordinateY)
    this.mouseDelta = p5.Vector.sub(currentMousePosition, this.lastMousePosition)
    this.lastMousePosition = currentMousePosition
  }

  enterMarker() {
    const marker = this.marker
    const state = marker.state
    const isHovered = state.isHovered

    if (isHovered === false) {
      state.setToHoveredState()
      if (typeof marker.markerMouseEnterCallback === 'function') marker.markerMouseEnterCallback(marker)
    }
  }

  leaveMarker() {
    const marker = this.marker
    const state = marker.state
    const isHovered = state.isHovered

    if (isHovered === true) {
      state.setToDehoveredState()
      if (typeof marker.markerMouseEnterCallback === 'function') marker.markerMouseLeaveCallback(marker)
    }
  }

  selectMarker() {
    const marker = this.marker
    const state = marker.state
    const isSelected = state.isSelected

    if (isSelected === false) {
      state.setToSelectedState()
      if (typeof marker.markerSelectedCallback === 'function') marker.markerSelectedCallback(marker)
    }
  }

  deselectMarker() {
    const marker = this.marker
    const state = marker.state
    const isSelected = state.isSelected
    const isSelectionLocked = state.isSelectionLocked
    const isDeselectionAllowed = isSelected === true && isSelectionLocked === false

    if (isDeselectionAllowed) {
      state.setToDeselectedState()
      if (typeof marker.markerDeselectedCallback === 'function') marker.markerDeselectedCallback(marker)
    }
  }

  /**
   * Called by p5 main event loop when mouse is pressed. Compares mouse
   * position returned from p5 to marker area, when mouse is
   * pressed, to determine if the marker is has been pressed
   */
  mouseClickedEvent(e: PointerEvent) {
    this.updateMousePosition()
    const marker = this.marker
    const state = marker.state
    const mode = state.mode

    if (mode !== MarkerModes.Create) {
      const markerMousedOver = marker.isMousedOver()
      const shouldSelect = markerMousedOver && marker.state.isSelected === false
      const shouldDeselect =
        (markerMousedOver && e.ctrlKey === true && marker.state.isSelected === true) ||
        (!markerMousedOver && e.ctrlKey === false)

      if (shouldSelect) this.selectMarker()
      else if (shouldDeselect) this.deselectMarker()
    }
  }

  /**
   * Called by p5 main event loop when mouse is dragged.If mouse is pressed on this marker
   * it will update the mouse delta.
   */
  mouseDraggedEvent() {
    this.updateMousePosition()
    const marker = this.marker
    if (!marker.state.isPressed && marker.state.mode !== MarkerModes.Create) return
    this.updateMouseDelta()
  }

  /**
   * Called by p5 main event loop when mouse is moved. If the marker is moused over, the marker hovered
   * callback function will be called. If marker is in create mode the mouse delta will be updated.
   */
  mouseMovedEvent() {
    this.updateMousePosition()
    const marker = this.marker
    const isInCreateMode = marker.state.mode === MarkerModes.Create

    if (!isInCreateMode) {
      if (marker.isMousedOver()) {
        this.enterMarker()
      }
      if (!marker.isMousedOver()) {
        this.leaveMarker()
      }
      return
    }

    this.updateMouseDelta()
  }

  /**
   * Called by p5 main event loop when mouse is pressed. Compares mouse
   * position returned from p5 to marker area, when mouse is
   * pressed, to determine if the marker is has been pressed
   */
  mousePressedEvent() {
    this.updateMousePosition()
    const marker = this.marker
    marker.state.pressedHandle = this.marker.handles.getMousedOverHandle()
    if (marker.isMousedOver() === true || marker.state.pressedHandle) {
      marker.state.isPressed = true
      this.updateMouseDelta()
    }
  }

  /**
   * Called by p5 main event loop when mouse is pressed. Compares mouse
   * position returned from p5 to marker area, when mouse is
   * released, to determine if the marker is has been released
   * and run side effects based on that check
   */
  mouseReleasedEvent() {
    this.updateMousePosition()
    const marker = this.marker
    marker.state.isPressed = false
    marker.state.pressedHandle = null
  }
}

export { BaseMarkerController }
